import React from "react";
import { Link } from "react-router-dom";

const AudienceInfo = ({
  title = "No audiences yet",
  ctaHref = "/research/new/",
  ctaLabel = "Create your first audience",
  showCta = true,
}) => {
  return (
    <div className="rounded-xl border border-theme-secondary bg-theme-secondary px-6 py-14 sm:px-10 sm:py-16 text-center max-w-2xl mx-auto">
      <h2 className="text-2xl sm:text-3xl font-semibold text-theme-text mb-4">{title}</h2>
      <p className="text-theme-text opacity-80 text-base leading-relaxed mb-8">
        An audience is a topic on a platform. Pick a topic and we'll populate high-fit creators and videos in minutes and analyze what works in this space.
      </p>
      <ul className="text-left max-w-md mx-auto space-y-4 mb-10">
        <li className="flex items-start gap-3">
          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border border-theme-secondary bg-theme-main text-theme-primary">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5} aria-hidden>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v9a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-3zM14 13a1 1 0 011-1h4a1 1 0 011 1v6a1 1 0 01-1 1h-4a1 1 0 01-1-1v-6z"
              />
            </svg>
          </span>
          <div>
            <p className="text-sm font-medium text-theme-text">Patterns</p>
            <p className="text-sm text-theme-text opacity-60 leading-snug">
              Spot recurring formats, hooks, and creative themes in your niche.
            </p>
          </div>
        </li>
        <li className="flex items-start gap-3">
          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border border-theme-secondary bg-theme-main text-theme-primary">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5} aria-hidden>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z"
              />
            </svg>
          </span>
          <div>
            <p className="text-sm font-medium text-theme-text">Analysis</p>
            <p className="text-sm text-theme-text opacity-60 leading-snug">
              Open a pattern in your niche, see how strong the trend is, and get a plain-language take on why it works
              for viewers.
            </p>
          </div>
        </li>
        <li className="flex items-start gap-3">
          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border border-theme-secondary bg-theme-main text-theme-primary">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5} aria-hidden>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z"
              />
            </svg>
          </span>
          <div>
            <p className="text-sm font-medium text-theme-text">Ideation</p>
            <p className="text-sm text-theme-text opacity-60 leading-snug">
              Generate fresh video ideas shaped around your brand and what this audience already responds to.
            </p>
          </div>
        </li>
        <li className="flex items-start gap-3">
          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border border-theme-secondary bg-theme-main text-theme-primary">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5} aria-hidden>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
              />
            </svg>
          </span>
          <div>
            <p className="text-sm font-medium text-theme-text">Script</p>
            <p className="text-sm text-theme-text opacity-60 leading-snug">
              Generate a script that sounds like your brand and leans on patterns that are more likely to perform for
              this audience.
            </p>
          </div>
        </li>
      </ul>
      {showCta ? (
        <Link
          to={ctaHref}
          className="inline-flex items-center justify-center px-8 py-3.5 text-base font-medium rounded-lg bg-theme-primary text-theme-button hover:opacity-90 transition-opacity"
        >
          {ctaLabel}
        </Link>
      ) : null}
    </div>
  );
};

export default AudienceInfo;
